import { Box, Fade, Typography } from '@mui/material';
import CardItemDTO from '@src/adapters/dto/card/CardItemDTO';
import { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import CardItemDetails from './CardItemDetails';
import { CardModificationDTO } from '@src/adapters/dto/card/CardModificationDTO';

interface Props {
  cardId: string;
  item: CardItemDTO;
  isEdit: boolean;
  onItemDeleted(id: string, modification: CardModificationDTO): void;
  onItemUpdated(cardItem: CardItemDTO, modification: CardModificationDTO): void;
}

function CardItem({
  cardId,
  item,
  isEdit,
  onItemDeleted,
  onItemUpdated,
}: Props) {
  const [hovered, setHovered] = useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);

  const handleItemDetailsClosed = (
    cardItem: CardItemDTO,
    modification: CardModificationDTO
  ) => {
    setEditDetailsOpen(false);
    onItemUpdated(cardItem, modification);
  };

  const handleItemDeleted = (id: string, modification: CardModificationDTO) => {
    setEditDetailsOpen(false);
    onItemDeleted(id, modification);
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          padding: theme.spacing(1),
          paddingLeft: theme.spacing(3),
          borderTop: '1px solid #E6E7E9',
          boxSizing: 'content-box',
          justifyContent: 'space-between',
        })}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="theme.pallete.warning.main">
              {`${item.inventory.name}${item.isLoan ? ' (Loan)' : ''}`}
            </Typography>
            <Box
              sx={{
                maxWidth: '150px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                })}>
                {item.note}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'left',
            gap: theme.spacing(1),
          })}>
          <Box
            sx={{
              width: '80px',
            }}>
            <Box>
              {item.qtyOpen > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ width: theme.spacing(8) })}>
                    Open:
                  </Typography>
                  <Typography variant="subtitle2">{item.qtyOpen}</Typography>
                </Box>
              )}
              {item.qtyOnHand > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <Typography
                    variant="body2"
                    sx={(theme) => ({ width: theme.spacing(8) })}>
                    On hand:
                  </Typography>
                  <Typography variant="subtitle2">{item.qtyOnHand}</Typography>
                </Box>
              )}
            </Box>
          </Box>
          {isEdit && (
            <Fade in={hovered} timeout={300}>
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  width: theme.spacing(3),
                })}>
                <EditIcon
                  onClick={() => setEditDetailsOpen(true)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
      <CardItemDetails
        cardId={cardId}
        item={item}
        open={editDetailsOpen}
        onCancel={() => setEditDetailsOpen(false)}
        onClose={handleItemDetailsClosed}
        onDelete={handleItemDeleted}
      />
    </>
  );
}

export default CardItem;
